import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { 
  MessageSquare, 
  ArrowLeft,
  Bell,
  Mail,
  Phone,
  Loader2,
  Save,
  Send,
  CheckCircle,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  const [settings, setSettings] = useState({
    email_notifications: true,
    sms_notifications: false,
    notification_phone: '',
    notify_on_new_lead: true,
    notify_on_viable_only: false,
    daily_digest: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/notifications/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notifications/settings`, settings);
      toast.success('Notification settings saved!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async () => {
    setTesting(true);
    try {
      const response = await axios.post(`${API}/notifications/test`);
      
      const results = response.data.results;
      let message = 'Test notification sent!';
      
      if (results.email === 'sent' && results.sms === 'sent') {
        message = 'Test email and SMS sent successfully!';
      } else if (results.email === 'sent') {
        message = 'Test email sent successfully!';
      } else if (results.sms === 'sent') {
        message = 'Test SMS sent successfully!';
      }
      
      toast.success(message);
    } catch (error) {
      console.error('Failed to send test notification:', error);
      toast.error('Failed to send test notification');
    } finally {
      setTesting(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-slate-600" data-testid="back-dashboard-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                IntakePal
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 
            className="text-3xl font-bold text-slate-900 mb-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Notification Settings
          </h1>
          <p className="text-slate-600">
            Get alerted instantly when new leads come in through your intake widget.
          </p>
        </div>

        <div className="space-y-6">
          {/* Email Notifications */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Email Notifications</CardTitle>
                    <CardDescription>Receive lead alerts at {user?.email}</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={settings.email_notifications}
                  onCheckedChange={(checked) => updateSetting('email_notifications', checked)}
                  data-testid="email-notifications-toggle"
                />
              </div>
            </CardHeader>
            {settings.email_notifications && (
              <CardContent className="pt-0">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex items-center gap-2 text-blue-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Email notifications are enabled</span>
                  </div>
                  <p className="text-sm text-blue-600 mt-1">
                    You&apos;ll receive emails at <strong>{user?.email}</strong> when new leads come in.
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* SMS Notifications */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>SMS Notifications</CardTitle>
                    <CardDescription>Get text alerts for new leads</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={settings.sms_notifications}
                  onCheckedChange={(checked) => updateSetting('sms_notifications', checked)}
                  data-testid="sms-notifications-toggle"
                />
              </div>
            </CardHeader>
            {settings.sms_notifications && (
              <CardContent className="pt-0 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number for SMS Alerts</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={settings.notification_phone}
                    onChange={(e) => updateSetting('notification_phone', e.target.value)}
                    data-testid="notification-phone-input"
                  />
                  <p className="text-xs text-slate-500">
                    Include country code (e.g., +1 for US). Standard messaging rates may apply.
                  </p>
                </div>
                {!settings.notification_phone && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2 text-amber-700">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm">Please enter a phone number to receive SMS alerts</span>
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* Notification Preferences */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Bell className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Notification Preferences</CardTitle>
                  <CardDescription>Customize when you want to be notified</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Notify on new lead */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">New Lead Alerts</p>
                  <p className="text-sm text-slate-500">Get notified when any new lead is submitted</p>
                </div>
                <Switch
                  checked={settings.notify_on_new_lead}
                  onCheckedChange={(checked) => updateSetting('notify_on_new_lead', checked)}
                  data-testid="notify-new-lead-toggle"
                />
              </div>

              {/* Viable only */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">High Priority Only</p>
                  <p className="text-sm text-slate-500">Only notify for high priority leads</p>
                </div>
                <Switch
                  checked={settings.notify_on_viable_only}
                  onCheckedChange={(checked) => updateSetting('notify_on_viable_only', checked)}
                  data-testid="viable-only-toggle"
                />
              </div>

              {/* Daily digest */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Daily Digest</p>
                  <p className="text-sm text-slate-500">Receive a daily summary instead of instant alerts</p>
                </div>
                <Switch
                  checked={settings.daily_digest}
                  onCheckedChange={(checked) => updateSetting('daily_digest', checked)}
                  data-testid="daily-digest-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4">
            <Button
              variant="outline"
              onClick={handleTestNotification}
              disabled={testing || (!settings.email_notifications && !settings.sms_notifications)}
              data-testid="test-notification-btn"
            >
              {testing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Test Notification
            </Button>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-teal-500 hover:bg-teal-600"
              data-testid="save-notifications-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Settings
            </Button>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 p-6 bg-teal-50 rounded-xl border border-teal-200">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-teal-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-teal-900 mb-1">Pro Tip</h3>
              <p className="text-teal-700 text-sm">
                Enable &quot;High Priority Only&quot; if you&apos;re receiving too many notifications. 
                You&apos;ll only be alerted for high priority leads - 
                helping you focus on the leads most likely to convert.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NotificationsPage;
